<script lang="ts" setup>
import Scrollbar from 'smooth-scrollbar';
import { onMounted, onUnmounted, ref } from 'vue';

const scrollContainerEl = ref<HTMLDivElement | null>(null);
const scrollBarInstance = ref<Scrollbar | null>(null);
const containerPaddings = ref({ left: 0, right: 0 });
const showLeftShadow = ref(false);
const showRightShadow = ref(false);
const trackRelativeSize = ref('100%');
const trackOffsetLeft = ref('0px');

const checkScrollShadowStates = () => {
  if (scrollBarInstance.value === null) return;

  showLeftShadow.value = scrollBarInstance.value.offset.x > containerPaddings.value.left;

  showRightShadow.value =
    scrollBarInstance.value.offset.x <
    scrollBarInstance.value.limit.x - containerPaddings.value.right;
};

// recalculateTrackSizeAndPosition reimplements a few calculations for thumb size library already has
// to make it respect content paddings
const recalculateTrackSizeAndPosition = () => {
  if (scrollBarInstance.value === null) return;

  // Size factor is taken from relation between container and contend sizes, this relation should be
  // applied to scroll thumb size because its size is representation of what content piece is current
  // in the viewport of the container
  const sizeFactor =
    scrollBarInstance.value.size.container.width / scrollBarInstance.value.size.content.width;
  trackRelativeSize.value = sizeFactor * 100 + '%';

  const paddings = containerPaddings.value.left + containerPaddings.value.right;
  // We take paddings into account so we could calculate scroll thumb offset based on real content size that is without paddings
  const containerWidthWithoutPaddings = scrollBarInstance.value.size.container.width - paddings;

  // Since thumb size to track size is relatively the same as container size to content size,
  // to calculate relative offset for thumb we take factor of content offset for content size,
  // This factor is the same as offset factor of thumb for track size.
  const relativeOffset =
    (scrollBarInstance.value.offset.x / scrollBarInstance.value.size.content.width) *
    containerWidthWithoutPaddings;

  trackOffsetLeft.value = relativeOffset + 'px';
};

const resizeObserver = new ResizeObserver(() => {
  if (scrollBarInstance.value === null || scrollContainerEl.value === null) return;

  // If content doesn't need scrolling - remove shadows
  if (scrollBarInstance.value.size.content.width === scrollBarInstance.value.size.container.width) {
    showLeftShadow.value = false;
    showRightShadow.value = false;
  } else {
    checkScrollShadowStates();
  }

  // Reconsider paddings for different styles based on media
  const containerStyles = getComputedStyle(scrollContainerEl.value);
  containerPaddings.value = {
    left: parseInt(containerStyles.paddingLeft, 10),
    right: parseInt(containerStyles.paddingRight, 10),
  };

  recalculateTrackSizeAndPosition();
});

onMounted(() => {
  if (scrollContainerEl.value === null) return;

  scrollBarInstance.value = Scrollbar.init(scrollContainerEl.value, {
    renderByPixels: true,
    continuousScrolling: true,
    alwaysShowTracks: true,
  });
  resizeObserver.observe(scrollBarInstance.value!.containerEl);
  resizeObserver.observe(scrollBarInstance.value!.contentEl);
  scrollBarInstance.value.addListener(checkScrollShadowStates);
  scrollBarInstance.value.addListener(recalculateTrackSizeAndPosition);
});
onUnmounted(() => {
  resizeObserver.disconnect();
  scrollBarInstance.value?.destroy();
});
</script>
<template>
  <div
    ref="scrollContainerEl"
    :class="[
      $style.scrollContainer,
      'relative overflow-clip pb-1 dark:[&_.scrollbar-thumb-x]:bg-gray-500',
      'before:pointer-events-none before:absolute before:bottom-1 before:left-0 before:top-0 before:z-10 before:w-3 before:bg-gradient-to-r before:from-gray-200 before:to-transparent before:transition before:content-[\'\'] dark:before:w-4 before:dark:from-navy-800',
      'after:pointer-events-none after:absolute after:bottom-1 after:right-0 after:top-0 after:w-3 after:bg-gradient-to-l after:from-gray-200 after:to-transparent after:transition after:content-[\'\'] dark:after:w-4 after:dark:from-navy-800',
      !showLeftShadow && 'before:opacity-0',
      !showRightShadow && 'after:opacity-0',
    ]"
  >
    <slot />
  </div>
</template>
<style module>
.scrollContainer {
  position: relative;
  :global(.scrollbar-track-x) {
    bottom: 8px;
    position: sticky;
    z-index: 2;
    background: none;
    margin-top: -8px;
    transform: translateY(4px);
  }
  :global(.scrollbar-thumb-x) {
    width: v-bind(trackRelativeSize) !important;
    transform: translateX(v-bind(trackOffsetLeft)) !important;
  }
  :global(.scrollbar-track-y) {
    display: none;
  }
}
</style>
